import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { BarChart3, ArrowRight, RefreshCw, Loader2, AlertTriangle, TrendingUp, Download, UserMinus } from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVENT_LABELS = {
  checkout_started: "Checkout started",
  trial_started: "Trial started",
  subscription_started: "Subscription (no trial)",
  trial_converted: "Trial → Paid",
  subscription_canceled: "Canceled",
};

const RANGES = [7, 30, 90, 365];

const StatCard = ({ label, value, sub, accent = "#00E676" }) => (
  <div
    className="rounded-2xl border border-white/5 bg-white/[0.02] px-5 py-5"
    data-testid={`funnel-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold">
      {label}
    </div>
    <div
      className="mt-2 text-3xl text-white font-semibold tracking-tight"
      style={{ fontFamily: "'Outfit', sans-serif", color: accent }}
    >
      {value}
    </div>
    {sub && <div className="mt-1 text-xs text-slate-300">{sub}</div>}
  </div>
);

const AdminAnalytics = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    if (!token) {
      setError("Missing ?token= in URL");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${API}/admin/analytics/funnel?token=${encodeURIComponent(token)}&days=${days}`
      );
      setData(res.data);
    } catch (e) {
      setError(
        e?.response?.status === 401
          ? "Invalid or missing admin token."
          : "Could not load analytics. Please try again."
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const counts = data?.counts || {};
  const rates = data?.rates || {};
  const totalPaid = data?.total_paid || 0;

  const funnelRows = useMemo(
    () => [
      { key: "checkout_started", value: counts.checkout_started || 0 },
      { key: "trial_started", value: counts.trial_started || 0 },
      { key: "subscription_started", value: counts.subscription_started || 0 },
      { key: "trial_converted", value: counts.trial_converted || 0 },
      { key: "subscription_canceled", value: counts.subscription_canceled || 0 },
    ],
    [counts]
  );
  const maxCount = Math.max(1, ...funnelRows.map((r) => r.value));

  return (
    <div className="min-h-screen bg-[#060B19] text-white" data-testid="admin-analytics-page">
      <header className="border-b border-white/5 bg-[#0A1226]/80 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#00E676]/10 border border-[#00E676]/30 flex items-center justify-center">
              <BarChart3 size={16} className="text-[#00E676]" />
            </div>
            <div>
              <div
                className="text-base sm:text-lg font-semibold tracking-tight"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Conversion Analytics
              </div>
              <div className="text-xs text-slate-400">CardWatch admin · funnel view</div>
            </div>
          </div>
          <Link
            to="/"
            className="text-xs text-slate-300 hover:text-white transition-colors"
            data-testid="admin-analytics-home-link"
          >
            ← Home
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 flex-wrap mb-6">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setDays(r)}
              data-testid={`funnel-range-${r}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                days === r
                  ? "border-[#00E676] bg-[#00E676]/10 text-[#00E676]"
                  : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/30"
              }`}
            >
              Last {r} days
            </button>
          ))}
          <button
            onClick={load}
            disabled={loading}
            data-testid="funnel-refresh"
            className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-white/10 hover:border-white/30 px-3 py-1.5 text-xs text-slate-300 hover:text-white transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh
          </button>
          <a
            href={`${API}/admin/analytics/export.csv?token=${encodeURIComponent(token)}&days=${days}`}
            data-testid="funnel-export-csv"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#00E676]/30 hover:border-[#00E676] bg-[#00E676]/10 hover:bg-[#00E676]/20 px-3 py-1.5 text-xs text-[#00E676] font-semibold transition-colors"
          >
            <Download size={12} />
            Download CSV
          </a>
        </div>

        {error && (
          <div
            className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300 flex items-center gap-2 mb-6"
            data-testid="funnel-error"
          >
            <AlertTriangle size={14} /> {error}
          </div>
        )}

        {data && (
          <>
            {data.mrr && (
              <div className="mb-8" data-testid="mrr-widget">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp size={14} className="text-[#00E676]" />
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold">
                    Monthly Recurring Revenue
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                  <StatCard
                    label="MRR"
                    value={`£${data.mrr.mrr.toFixed(2)}`}
                    sub={`Across ${data.mrr.active_subscribers} active ${
                      data.mrr.active_subscribers === 1 ? "subscriber" : "subscribers"
                    }`}
                  />
                  <StatCard
                    label="ARR"
                    value={`£${data.mrr.arr.toFixed(2)}`}
                    sub="MRR × 12"
                    accent="#7C9CFF"
                  />
                  <StatCard
                    label="Monthly plan"
                    value={`£${(data.mrr.mrr_by_plan?.cardwatch_pro_monthly || 0).toFixed(2)}`}
                    sub="£4.99 × subs on monthly"
                    accent="#00E676"
                  />
                  <StatCard
                    label="Yearly plan"
                    value={`£${(data.mrr.mrr_by_plan?.cardwatch_pro_yearly || 0).toFixed(2)}`}
                    sub="£49.99/12 × subs on yearly"
                    accent="#F5B841"
                  />
                </div>
                <div
                  className="rounded-2xl border border-white/5 bg-white/[0.02] px-4 py-4"
                  data-testid="mrr-chart"
                >
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold mb-3 px-1">
                    MRR trend · last {data.range_days} days
                  </div>
                  <div style={{ width: "100%", height: 200 }}>
                    <ResponsiveContainer>
                      <AreaChart
                        data={data.mrr.series || []}
                        margin={{ top: 8, right: 12, left: -18, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="mrrFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#00E676" stopOpacity={0.55} />
                            <stop offset="100%" stopColor="#00E676" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                        <XAxis
                          dataKey="date"
                          stroke="rgba(148,163,184,0.6)"
                          tick={{ fill: "#94A3B8", fontSize: 10 }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke="rgba(148,163,184,0.6)"
                          tick={{ fill: "#94A3B8", fontSize: 10 }}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(v) => `£${v}`}
                          width={50}
                        />
                        <Tooltip
                          contentStyle={{
                            background: "#0E172C",
                            border: "1px solid #1A2849",
                            borderRadius: 12,
                            fontSize: 12,
                            color: "#E6EAF2",
                          }}
                          formatter={(v, k) =>
                            k === "mrr" ? [`£${v}`, "MRR"] : [v, "Active subs"]
                          }
                          labelStyle={{ color: "#94A3B8", fontSize: 11 }}
                        />
                        <Area
                          type="monotone"
                          dataKey="mrr"
                          stroke="#00E676"
                          strokeWidth={2}
                          fill="url(#mrrFill)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {data.churn && (
              <div className="mb-8" data-testid="churn-widget">
                <div className="flex items-center gap-2 mb-4">
                  <UserMinus size={14} className="text-[#EF4444]" />
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold">
                    Churn Rate
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-5">
                  <StatCard
                    label={`Churn · last ${data.range_days}d`}
                    value={
                      data.churn.period?.rate == null
                        ? "—"
                        : `${data.churn.period.rate}%`
                    }
                    sub={
                      data.churn.period?.rate == null
                        ? "No active subs at start of range"
                        : `${data.churn.period.cancels} of ${data.churn.period.active_at_start} lost`
                    }
                    accent={
                      data.churn.period?.rate == null
                        ? "#94A3B8"
                        : data.churn.period.rate >= 10
                        ? "#EF4444"
                        : data.churn.period.rate >= 5
                        ? "#F5B841"
                        : "#00E676"
                    }
                  />
                  <StatCard
                    label="Cancels in range"
                    value={(data.churn.period?.cancels || 0).toLocaleString()}
                    sub="From cohort active at range start"
                    accent="#EF4444"
                  />
                  <StatCard
                    label="Active at range start"
                    value={(data.churn.period?.active_at_start || 0).toLocaleString()}
                    sub="Denominator for churn rate"
                    accent="#7C9CFF"
                  />
                </div>
                <div
                  className="rounded-2xl border border-white/5 bg-white/[0.02] px-4 py-4"
                  data-testid="churn-monthly-chart"
                >
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold mb-3 px-1 flex items-center justify-between">
                    <span>Monthly churn trend · last 6 months</span>
                    <span className="text-slate-500 normal-case tracking-normal text-[10px] font-normal">
                      lower is better
                    </span>
                  </div>
                  <div style={{ width: "100%", height: 200 }}>
                    <ResponsiveContainer>
                      <BarChart
                        data={(data.churn.months || []).map((m) => ({
                          ...m,
                          rateNum: m.rate == null ? 0 : m.rate,
                        }))}
                        margin={{ top: 8, right: 12, left: -18, bottom: 0 }}
                      >
                        <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                        <XAxis
                          dataKey="label"
                          stroke="rgba(148,163,184,0.6)"
                          tick={{ fill: "#94A3B8", fontSize: 10 }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke="rgba(148,163,184,0.6)"
                          tick={{ fill: "#94A3B8", fontSize: 10 }}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(v) => `${v}%`}
                          width={50}
                        />
                        <Tooltip
                          cursor={{ fill: "rgba(255,255,255,0.03)" }}
                          contentStyle={{
                            background: "#0E172C",
                            border: "1px solid #1A2849",
                            borderRadius: 12,
                            fontSize: 12,
                            color: "#E6EAF2",
                          }}
                          formatter={(_v, _k, item) => {
                            const p = item?.payload || {};
                            if (p.rate == null) {
                              return ["No active subs", "Churn rate"];
                            }
                            return [
                              `${p.rate}% · ${p.cancels}/${p.active_at_start}`,
                              "Churn rate",
                            ];
                          }}
                          labelStyle={{ color: "#94A3B8", fontSize: 11 }}
                        />
                        <Bar dataKey="rateNum" radius={[6, 6, 0, 0]}>
                          {(data.churn.months || []).map((m, idx) => (
                            <Cell
                              key={idx}
                              fill={
                                m.rate == null
                                  ? "rgba(148,163,184,0.25)"
                                  : m.rate >= 10
                                  ? "#EF4444"
                                  : m.rate >= 5
                                  ? "#F5B841"
                                  : "#00E676"
                              }
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={14} className="text-[#7C9CFF]" />
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold">
                Conversion Funnel
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              <StatCard
                label="Total Sign-ups"
                value={(data.signups?.total_all_time || 0).toLocaleString()}
                sub={`+${data.signups?.in_range || 0} in last ${data.range_days}d`}
                accent="#7C9CFF"
              />
              <StatCard
                label="Active Paid Subs"
                value={(data.mrr?.active_subscribers || 0).toLocaleString()}
                sub="Not canceled"
              />
              <StatCard
                label="Free → Paid"
                value={
                  data.signups?.total_all_time
                    ? `${((totalPaid / data.signups.total_all_time) * 100).toFixed(1)}%`
                    : "—"
                }
                sub={`${totalPaid} of ${data.signups?.total_all_time || 0} sign-ups`}
                accent="#00E676"
              />
              <StatCard
                label="Canceled"
                value={counts.subscription_canceled || 0}
                sub="Trial expired + user-canceled"
                accent="#EF4444"
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              <StatCard
                label="Checkout → Trial"
                value={rates.checkout_to_trial == null ? "—" : `${rates.checkout_to_trial}%`}
                sub={`${counts.trial_started || 0} of ${counts.checkout_started || 0}`}
              />
              <StatCard
                label="Trial → Paid"
                value={rates.trial_to_paid == null ? "—" : `${rates.trial_to_paid}%`}
                sub={`${counts.trial_converted || 0} of ${counts.trial_started || 0}`}
              />
              <StatCard
                label="Checkout → Paid"
                value={rates.checkout_to_paid == null ? "—" : `${rates.checkout_to_paid}%`}
                sub={`${totalPaid} of ${counts.checkout_started || 0}`}
                accent="#7C9CFF"
              />
            </div>

            <div className="rounded-2xl border border-white/5 bg-white/[0.02] px-5 py-5 mb-8">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold mb-4">
                Funnel counts
              </div>
              <ul className="space-y-3" data-testid="funnel-counts-list">
                {funnelRows.map((r) => (
                  <li key={r.key} data-testid={`funnel-row-${r.key}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-slate-200 font-medium">{EVENT_LABELS[r.key]}</span>
                      <span
                        className="text-white font-semibold"
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                      >
                        {r.value}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className="h-full transition-[width] duration-500 ease-out"
                        style={{
                          width: `${(r.value / maxCount) * 100}%`,
                          background:
                            r.key === "subscription_canceled"
                              ? "linear-gradient(90deg, #EF4444, #B91C1C)"
                              : "linear-gradient(90deg, #00E676, #00A65B)",
                        }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-white/5 bg-white/[0.02] px-5 py-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold mb-4">
                Recent events (last 10)
              </div>
              {data.recent?.length ? (
                <ul className="divide-y divide-white/5" data-testid="funnel-recent-list">
                  {data.recent.map((ev, i) => (
                    <li
                      key={`${ev.ts}-${i}`}
                      className="py-2.5 flex items-center gap-3 text-xs"
                      data-testid={`funnel-recent-${i}`}
                    >
                      <span
                        className="px-2 py-0.5 rounded-full uppercase tracking-widest text-[9px] font-semibold border"
                        style={{
                          color:
                            ev.event_type === "trial_converted"
                              ? "#00E676"
                              : ev.event_type === "subscription_canceled"
                              ? "#EF4444"
                              : "#7C9CFF",
                          borderColor:
                            ev.event_type === "trial_converted"
                              ? "rgba(0,230,118,0.35)"
                              : ev.event_type === "subscription_canceled"
                              ? "rgba(239,68,68,0.35)"
                              : "rgba(124,156,255,0.35)",
                        }}
                      >
                        {ev.event_type}
                      </span>
                      <span className="text-slate-300 truncate flex-1">
                        <span
                          className="text-slate-100 font-medium"
                          title={ev.user_id || ""}
                        >
                          {ev.name || ev.email || ev.user_id || "—"}
                        </span>
                        {ev.email && ev.name && (
                          <span className="text-slate-500"> · {ev.email}</span>
                        )}
                        {ev.lookup_key && (
                          <>
                            {" · "}
                            <span className="text-slate-400">{ev.lookup_key}</span>
                          </>
                        )}
                      </span>
                      <span className="text-slate-400 tabular-nums whitespace-nowrap">
                        {ev.ts?.slice(0, 16).replace("T", " ")}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-slate-400 text-center py-6">
                  No events in this range yet.
                </div>
              )}
            </div>

            <div className="mt-6 text-[11px] text-slate-500 flex items-center gap-1.5">
              <ArrowRight size={11} /> Events logged since {data.since?.slice(0, 10)} ·
              range: last {data.range_days} days
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminAnalytics;
